import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  TextInput,
  Platform,
  Image,
  Modal,
  Dimensions,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { collection, getDocs, addDoc, query, where, doc, updateDoc, limit, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { fonts } from '../utils/fonts';
import { WebView } from 'react-native-webview';

// Bank icons mapping
const bankIcons = {
  cbe: require('../../assets/banks/cbe.jpeg'),
  cbebirr: require('../../assets/banks/cbebirr.jpeg'),
  ethioswitch: require('../../assets/banks/ethio_switch.png'),
  abyssinia: require('../../assets/banks/abyssinia.png'),
  dashen: require('../../assets/banks/dashen.png'),
  telebirr: require('../../assets/banks/telebirr.jpeg'),
  awash: require('../../assets/banks/awash.png'),
};

// Helper function to get bank icon key from bank name or ID
const getBankIconKey = (bankIdOrName: string): keyof typeof bankIcons | null => {
  const normalized = bankIdOrName.toLowerCase();
  
  if (normalized.includes('ethioswitch') || normalized.includes('ethio switch')) return 'ethioswitch';
  if (normalized.includes('cbebirr') || normalized.includes('cbe birr')) return 'cbebirr';
  if (normalized.includes('commercial') || normalized.includes('cbe')) return 'cbe';
  if (normalized.includes('abyssinia')) return 'abyssinia';
  if (normalized.includes('dashen')) return 'dashen';
  if (normalized.includes('telebirr') || normalized.includes('tele birr')) return 'telebirr';
  if (normalized.includes('awash')) return 'awash';
  
  return null;
};

const colors = {
  beeYellow: '#F4C430',
  honeyGold: '#D4AF37',
  darkYellow: '#B8860B',
  deepNavy: '#1A1B3E',
  charcoal: '#2C2C2C',
  cream: '#FFF9E6',
  white: '#FFFFFF',
  warmGray: '#6B6B6B',
  lightGray: '#F5F5F5',
  softBlue: '#4A90E2',
  success: '#27AE60',
  danger: '#E74C3C',
};

interface Bank {
  id: string;
  name: string;
  accountHolderName: string;
  accountNumber: string;
  isActive: boolean;
  icon?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  priceMonthly: number;
  priceYearly: number;
  color: string;
  icon: string;
  features?: { text: string; enabled: boolean }[];
}

// Subscription plan configurations
const SUBSCRIPTION_PLANS = [
  {
    id: 'free',
    name: 'Free',
    priceMonthly: 0,
    priceYearly: 0,
    color: '#6B6B6B',
    icon: 'person' as const,
  },
  {
    id: 'standard',
    name: 'Standard',
    priceMonthly: 299,
    priceYearly: 2990,
    color: '#D4AF37',
    icon: 'star' as const,
  },
  {
    id: 'pro',
    name: 'Pro',
    priceMonthly: 4999,
    priceYearly: 49990,
    color: '#7C3AED',
    icon: 'workspace-premium' as const,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceMonthly: 15000,
    priceYearly: 150000,
    color: '#4A90E2',
    icon: 'business' as const,
  },
];

const PaymentScreen: React.FC<any> = ({ isDarkMode, colors: parentColors }) => {
  const navigation = useNavigation();
  const route = useRoute();
  const { plan: planId, returnTo } = route.params as { plan: string; returnTo?: string };
  
  const user = auth.currentUser;
  const [userData, setUserData] = useState<any>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>(SUBSCRIPTION_PLANS);
  const [subscription, setSubscription] = useState<SubscriptionPlan>(SUBSCRIPTION_PLANS.find(p => p.id === planId) || SUBSCRIPTION_PLANS[1]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [paymentPeriod, setPaymentPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [isBankDropdownOpen, setIsBankDropdownOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    fetchSubscriptionPlans();
    fetchBanks();
    // generate a unique payment reference starting with 'N' followed by 7 alphanumeric chars
    (async () => {
      await generateUniquePaymentReference();
    })();
    
    // Dimensions listener
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    
    // Fetch user data
    const fetchUserData = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            // Pre-populate name and phone from user profile
            if (data.name && !userName) {
              setUserName(data.name);
            } else if (data.displayName && !userName) {
              setUserName(data.displayName);
            } else if (user.displayName && !userName) {
              setUserName(user.displayName);
            }
            if (data.phone && !userPhone) {
              setUserPhone(data.phone);
            } else if (data.phoneNumber && !userPhone) {
              setUserPhone(data.phoneNumber);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
    
    return () => {
      subscription?.remove();
    };
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      const plansSnapshot = await getDocs(query(collection(db, 'subscriptionPlans'), limit(20)));
      
      if (!plansSnapshot.empty) {
        const planDefaults: Record<string, { color: string; icon: string }> = {
          free: { color: '#6B6B6B', icon: 'person' },
          standard: { color: '#D4AF37', icon: 'star' },
          pro: { color: '#7C3AED', icon: 'workspace-premium' },
          enterprise: { color: '#4A90E2', icon: 'business' },
        };
        const firestorePlans: SubscriptionPlan[] = plansSnapshot.docs.map(doc => {
          const data = doc.data();
          const defaults = planDefaults[doc.id] || { color: '#6B6B6B', icon: 'star' };
          return {
            id: doc.id,
            name: data.name,
            priceMonthly: data.priceMonthly || 0,
            priceYearly: data.priceYearly || 0,
            color: data.color || defaults.color,
            icon: data.icon || defaults.icon,
            features: (data.features || []).map((f: any) => ({
              text: f.name || f.text || '',
              enabled: f.enabled !== undefined ? f.enabled : true
            }))
          };
        });
        
        setSubscriptionPlans(firestorePlans);
        
        // Update the current subscription plan with Firestore data
        const currentPlan = firestorePlans.find(p => p.id === planId);
        if (currentPlan) {
          setSubscription(currentPlan);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      // Keep the default SUBSCRIPTION_PLANS if loading fails
    }
  };

  const generateUniquePaymentReference = async (): Promise<string> => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let reference = 'N';
      for (let i = 0; i < 7; i++) {
        reference += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      // Check uniqueness in Firestore
      try {
        const q = query(collection(db, 'paymentRequests'), where('paymentReference', '==', reference));
        const snap = await getDocs(q);
        if (snap.empty) {
          setPaymentReference(reference);
          return reference;
        }
      } catch (err) {
        console.warn('Error checking payment reference uniqueness, proceeding with generated value', err);
        setPaymentReference(reference);
        return reference;
      }
    }

    // Fallback: use timestamp-based ref
    const fallback = 'N' + Math.random().toString(36).replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 7);
    setPaymentReference(fallback);
    return fallback;
  };

  const fetchBanks = async () => {
    try {
      const banksQuery = query(
        collection(db, 'banks'),
        where('isActive', '==', true)
      );
      const banksSnapshot = await getDocs(banksQuery);
      const fetchedBanks = banksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Bank[];
      
      setBanks(fetchedBanks);
      
      // If no banks in Firestore, use default Ethiopian banks
      if (fetchedBanks.length === 0) {
        console.log('No banks found in Firestore, using default banks');
        const defaultBanks: Bank[] = [
          {
            id: 'cbe',
            name: 'Commercial Bank of Ethiopia',
            accountHolderName: 'NibJobs',
            accountNumber: '1000000000000',
            isActive: true,
            icon: 'account-balance',
          },
          {
            id: 'abyssinia',
            name: 'Abyssinia Bank',
            accountHolderName: 'NibJobs',
            accountNumber: '2000000000000',
            isActive: true,
            icon: 'account-balance',
          },
          {
            id: 'dashen',
            name: 'Dashen Bank',
            accountHolderName: 'NibJobs',
            accountNumber: '3000000000000',
            isActive: true,
            icon: 'account-balance',
          },
          {
            id: 'telebirr',
            name: 'Tele Birr',
            accountHolderName: 'NibJobs',
            accountNumber: '0900000000',
            isActive: true,
            icon: 'phone-android',
          },
          {
            id: 'awash',
            name: 'Awash Bank',
            accountHolderName: 'NibJobs',
            accountNumber: '4000000000000',
            isActive: true,
            icon: 'account-balance',
          },
        ];
        setBanks(defaultBanks);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
      // Use all 5 default banks on error
      const defaultBanks: Bank[] = [
        {
          id: 'cbe',
          name: 'Commercial Bank of Ethiopia',
          accountHolderName: 'NibJobs',
          accountNumber: '1000000000000',
          isActive: true,
          icon: 'account-balance',
        },
        {
          id: 'abyssinia',
          name: 'Abyssinia Bank',
          accountHolderName: 'NibJobs',
          accountNumber: '2000000000000',
          isActive: true,
          icon: 'account-balance',
        },
        {
          id: 'dashen',
          name: 'Dashen Bank',
          accountHolderName: 'NibJobs',
          accountNumber: '3000000000000',
          isActive: true,
          icon: 'account-balance',
        },
        {
          id: 'telebirr',
          name: 'Tele Birr',
          accountHolderName: 'NibJobs',
          accountNumber: '0900000000',
          isActive: true,
          icon: 'phone-android',
        },
        {
          id: 'awash',
          name: 'Awash Bank',
          accountHolderName: 'NibJobs',
          accountNumber: '4000000000000',
          isActive: true,
          icon: 'account-balance',
        },
      ];
      setBanks(defaultBanks);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPayment = async () => {
    // Prevent double submission
    if (submitting) {
      return;
    }
    
    if (!selectedBank) {
      Alert.alert('Select Bank', 'Please select a payment method');
      return;
    }

    // Check if payment method is CBEBirr, EthioSwitch, or TeleBirr (gateway payments)
    const isCBEBirr = selectedBank.name.toLowerCase().includes('cbebirr') || 
                      selectedBank.name.toLowerCase().includes('cbe birr');
    const isEthioSwitch = selectedBank.name.toLowerCase().includes('ethioswitch') ||
                          selectedBank.name.toLowerCase().includes('ethio switch');
    const isTeleBirr = selectedBank.name.toLowerCase().includes('telebirr') ||
                       selectedBank.name.toLowerCase().includes('tele birr');
    const isGatewayPayment = isCBEBirr || isEthioSwitch || isTeleBirr;

    // Only validate name and phone for non-gateway payments
    if (!isGatewayPayment) {
      if (!userName.trim()) {
        Alert.alert('Name Required', 'Please enter your full name');
        return;
      }

      if (!userPhone.trim()) {
        Alert.alert('Phone Required', 'Please enter your phone number');
        return;
      }
    }

    setSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        Alert.alert('Error', 'You must be logged in to make a payment');
        setSubmitting(false);
        return;
      }

      const amount = paymentPeriod === 'monthly' ? subscription.priceMonthly : subscription.priceYearly;

      // Initialize payment with pending status immediately
      const initialPaymentDoc = await addDoc(collection(db, 'paymentRequests'), {
        userId: user.uid,
        userEmail: user.email,
        userName: userName.trim() || userData?.name || userData?.displayName || user.displayName || user.email?.split('@')[0] || 'User',
        userPhone: userPhone.trim() || userData?.phone || userData?.phoneNumber || 'N/A',
        subscriptionId: subscription.id,
        subscriptionName: subscription.name,
        amount: amount,
        paymentPeriod: paymentPeriod,
        bankId: selectedBank.id,
        bankName: selectedBank.name,
        accountNumber: selectedBank.accountNumber || 'N/A',
        accountHolderName: selectedBank.accountHolderName || 'N/A',
        paymentReference: paymentReference,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      console.log('Payment initialized with pending status:', initialPaymentDoc.id);

      if (isCBEBirr || isEthioSwitch || isTeleBirr) {
        // Handle gateway payment (CBEBirr, EthioSwitch, or TeleBirr) via API
        const gatewayName = isTeleBirr ? 'TeleBirr' : (isEthioSwitch ? 'EthioSwitch' : 'CBEBirr');
        const apiEndpoint = isTeleBirr
          ? 'https://payapi.nibjobs.com/payment/telebirr/initiate'
          : (isEthioSwitch 
            ? 'https://payment-api-453177272540.us-central1.run.app/payment/ethioswitch/generate-url'
            : 'https://payment-api-453177272540.us-central1.run.app/payment/cbebirr/generate-url');
        const apiKey = isTeleBirr
          ? 'telebirr-innovatives-ts-2025-E5E8C6564AFD7CD2CECB74AA2CA5A'
          : (isEthioSwitch
            ? 'ethioswitch-innovatives-ts-2025-secure-E5E8C6564AFD7CD2CECB74AA2CA5A'
            : 'cbebirr-innovatives-ts-2025-secure-B16D8E4F9A2C7D5E1F3A8B9C4D6E7F8A');
        
        try {
          console.log(`Initiating ${gatewayName} payment:`, { paymentReference, amount });
          
          // Get Firebase Auth token for authentication
          const idToken = await user.getIdToken();
          
          // Prepare request body based on gateway type
          const requestBody = isTeleBirr ? {
            amount: amount,
            orderId: paymentReference,
            subject: `${subscription.name} Subscription - ${paymentPeriod}`,
            customerName: userName.trim() || userData?.name || userData?.displayName || user.displayName || 'User',
            customerPhone: userPhone.trim() || userData?.phone || userData?.phoneNumber || '',
            customerEmail: user.email || '',
            metadata: {
              userId: user.uid,
              subscriptionId: subscription.id,
              subscriptionName: subscription.name,
              paymentPeriod: paymentPeriod,
            }
          } : {
            paymentReference: paymentReference,
            amount: amount,
            userId: user.uid,
            userEmail: user.email
          };
          
          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
              'x-api-key': apiKey
            },
            body: JSON.stringify(requestBody)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('API error response:', errorText);
            throw new Error(`API responded with status ${response.status}: ${errorText}`);
          }

          const data = await response.json();
          console.log(`${gatewayName} API response:`, data);

          if (data.paymentUrl) {
            console.log('Payment URL received, redirecting to:', data.paymentUrl);
            
            // Validate the URL format
            if (!data.paymentUrl.startsWith('http://') && !data.paymentUrl.startsWith('https://')) {
              throw new Error('Invalid payment URL format received');
            }
            
            // Update payment request with payment URL
            await updateDoc(doc(db, 'paymentRequests', initialPaymentDoc.id), {
              paymentUrl: data.paymentUrl,
              updatedAt: new Date(),
            });
            
            console.log('Payment request updated with payment URL');

            // Open payment URL in new tab for web, app link for mobile
            console.log('Opening payment gateway in new tab...');
            
            // Open payment URL in new tab (not popup to avoid browser blockers)
            if (Platform.OS === 'web') {
              window.open(data.paymentUrl, '_blank');
            } else {
              Linking.openURL(data.paymentUrl);
            }
            
            setSubmitting(false);
            
            // Show info alert after opening payment
            setTimeout(() => {
              Alert.alert(
                'Payment Gateway Opened',
                'Complete your payment in the new window. After payment, return here and check your subscription status.',
                [
                  { text: 'OK', onPress: () => navigation.goBack() }
                ]
              );
            }, 500);
            
            return;
          } else {
            console.error('No payment URL in response:', data);
            throw new Error(`Payment URL not received from ${gatewayName} API`);
          }
        } catch (apiError) {
          console.error(`${gatewayName} API error:`, apiError);
          setSubmitting(false);
          Alert.alert(
            'Payment Error',
            `Failed to initiate ${gatewayName} payment. Please try again or contact support.`
          );
          return;
        }
      }

      // Regular payment flow for other banks - payment already initialized above
      console.log('Payment request saved successfully:', initialPaymentDoc.id);
      setSubmitting(false);

      // Store success data and show modal
      setSuccessData({
        reference: paymentReference,
        amount: amount,
        bankName: selectedBank.name,
        userEmail: user.email
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting payment:', error);
      setSubmitting(false);
      Alert.alert(
        'Error',
        'Failed to submit payment request. Please try again or contact support if the problem persists.'
      );
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.beeYellow} />
          <Text style={styles.loadingText}>Loading payment methods...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Toolbar */}
      <View style={[styles.jobsToolbar, { 
        backgroundColor: parentColors.white,
        borderBottomColor: isDarkMode ? parentColors.mediumGray : '#E5E7EB'
      }]}>
        <View style={styles.toolbarLeft}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.toolbarIconButton}
          >
            <Icon name="arrow-back" size={24} color={parentColors.deepNavy} />
          </TouchableOpacity>
          <Text style={[styles.toolbarTitle, { color: parentColors.deepNavy }]}>
            Payment
          </Text>
        </View>
        
        <View style={styles.toolbarRight}>
          {user && userData && (() => {
            const subscriptionId = (userData?.subscriptionId || userData?.subscriptionPlan || 'free').toLowerCase();
            const plan = SUBSCRIPTION_PLANS.find(p => p.id === subscriptionId) || SUBSCRIPTION_PLANS[0];
            return (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: plan.color + '20',
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 16,
                marginRight: 12
              }}>
                <Icon name={plan.icon as any} size={14} color={plan.color} />
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: plan.color,
                  marginLeft: 6,
                  fontFamily: fonts.semiBold
                }}>
                  {plan.name}
                </Text>
              </View>
            );
          })()}
          
          <TouchableOpacity
            onPress={() => navigation.navigate('Jobs' as never)}
            style={styles.toolbarIconButton}
          >
            <Icon name="work" size={24} color={parentColors.deepNavy} />
          </TouchableOpacity>
          
          <TouchableOpacity
            onPress={() => navigation.navigate('Settings' as never)}
            style={styles.toolbarIconButton}
          >
            <Icon name="settings" size={24} color={parentColors.deepNavy} />
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Navigation Menu */}
      {isMoreMenuOpen && (
        <View style={[styles.mobileMenu, { backgroundColor: parentColors.white }]}>
          <TouchableOpacity
            style={styles.mobileMenuItem}
            onPress={() => {
              setIsMoreMenuOpen(false);
              navigation.navigate('Jobs' as never);
            }}
          >
            <Icon name="work" size={20} color={parentColors.deepNavy} />
            <Text style={[styles.mobileMenuText, { color: parentColors.deepNavy }]}>Browse Jobs</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.mobileMenuItem}
            onPress={() => {
              setIsMoreMenuOpen(false);
              navigation.navigate('Settings' as never);
            }}
          >
            <Icon name="settings" size={20} color={parentColors.deepNavy} />
            <Text style={[styles.mobileMenuText, { color: parentColors.deepNavy }]}>Settings</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.mobileMenuItem}
            onPress={() => {
              setIsMoreMenuOpen(false);
              navigation.goBack();
            }}
          >
            <Icon name="arrow-back" size={20} color={parentColors.deepNavy} />
            <Text style={[styles.mobileMenuText, { color: parentColors.deepNavy }]}>Go Back</Text>
          </TouchableOpacity>
        </View>
      )}

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={{ alignItems: 'center', paddingVertical: 20 }}>
          <View style={{ width: '100%', maxWidth: 520, paddingHorizontal: 20 }}>
            {/* Hero Section */}
            <View style={{ backgroundColor: colors.deepNavy, borderRadius: 16, padding: 24, marginBottom: 20, alignItems: 'center' }}>
              <Icon name="payment" size={40} color={colors.white} style={{ marginBottom: 12 }} />
              <Text style={{ fontSize: 24, fontWeight: '700', color: colors.white, textAlign: 'center', marginBottom: 6 }}>
                Complete Your Payment
              </Text>
              <Text style={{ fontSize: 14, color: colors.cream, opacity: 0.9, textAlign: 'center' }}>
                Secure checkout for {subscription.name} plan
              </Text>
            </View>

            {/* Payment Reference Card - Only show for non-gateway banks */}
            {selectedBank && !(
              selectedBank.name.toLowerCase().includes('cbebirr') || 
              selectedBank.name.toLowerCase().includes('cbe birr') ||
              selectedBank.name.toLowerCase().includes('ethioswitch') ||
              selectedBank.name.toLowerCase().includes('ethio switch') ||
              selectedBank.name.toLowerCase().includes('telebirr') ||
              selectedBank.name.toLowerCase().includes('tele birr')
            ) && (
              <View style={styles.referenceCard}>
                <View style={styles.referenceHeader}>
                  <Icon name="receipt-long" size={20} color={colors.beeYellow} />
                  <Text style={styles.referenceLabel}>Payment Reference</Text>
                </View>
                <View style={styles.referenceBox}>
                  <Text style={styles.referenceCode}>{paymentReference}</Text>
                </View>
                <View style={styles.referenceInfo}>
                  <Icon name="info-outline" size={14} color={colors.warmGray} />
                  <Text style={styles.referenceHint}>Use this code for your bank transfer</Text>
                </View>
              </View>
            )}

            {/* Subscription & Amount Card */}
            <View style={styles.pricingCard}>
              <View style={styles.pricingHeader}>
                <Icon name="star" size={18} color={colors.beeYellow} />
                <Text style={styles.pricingTitle}>Selected Plan</Text>
              </View>
              
              <View style={styles.planDetails}>
                <View style={styles.planBadge}>
                  <Icon name="grade" size={16} color={colors.deepNavy} />
                  <Text style={styles.planName}>{subscription.name}</Text>
                </View>
                
                <View style={styles.amountContainer}>
                  <Text style={styles.amountValue}>
                    {paymentPeriod === 'monthly' ? subscription.priceMonthly : subscription.priceYearly}
                  </Text>
                  <Text style={styles.amountCurrency}>ETB</Text>
                </View>
              </View>

              {/* Period Toggle */}
              {subscription.priceYearly > 0 && (
                <View style={styles.periodSection}>
                  <Text style={styles.periodLabel}>Billing Period</Text>
                  <View style={styles.toggleContainer}>
                    <TouchableOpacity
                      style={[styles.toggleButton, paymentPeriod === 'monthly' && styles.toggleButtonActive]}
                      onPress={() => setPaymentPeriod('monthly')}
                    >
                      <Icon 
                        name="calendar-today" 
                        size={14} 
                        color={paymentPeriod === 'monthly' ? colors.beeYellow : colors.warmGray} 
                      />
                      <Text style={[styles.toggleText, paymentPeriod === 'monthly' && styles.toggleTextActive]}>
                        Monthly
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.toggleButton, paymentPeriod === 'yearly' && styles.toggleButtonActive]}
                      onPress={() => setPaymentPeriod('yearly')}
                    >
                      <Icon 
                        name="event" 
                        size={14} 
                        color={paymentPeriod === 'yearly' ? colors.beeYellow : colors.warmGray} 
                      />
                      <Text style={[styles.toggleText, paymentPeriod === 'yearly' && styles.toggleTextActive]}>
                        Yearly
                      </Text>
                      {subscription.priceYearly < subscription.priceMonthly * 12 && (
                        <View style={styles.savingBadge}>
                          <Text style={styles.savingText}>SAVE</Text>
                        </View>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>

            {/* User Information - Only show for non-gateway banks (not CBEBirr, EthioSwitch, or TeleBirr) */}
            {selectedBank && !(
              selectedBank.name.toLowerCase().includes('cbebirr') || 
              selectedBank.name.toLowerCase().includes('cbe birr') ||
              selectedBank.name.toLowerCase().includes('ethioswitch') ||
              selectedBank.name.toLowerCase().includes('ethio switch') ||
              selectedBank.name.toLowerCase().includes('telebirr')
            ) && (
              <View style={styles.infoCard}>
                <View style={styles.cardHeader}>
                  <Icon name="person-outline" size={18} color={colors.beeYellow} />
                  <Text style={styles.cardTitle}>Your Information</Text>
                </View>
                <View style={styles.inputGroup}>
                  <View style={styles.inputWrapper}>
                    <Icon name="badge" size={18} color={colors.warmGray} style={styles.inputIcon} />
                    <TextInput
                      style={styles.input}
                      placeholder="Full Name"
                      value={userName}
                      onChangeText={setUserName}
                      placeholderTextColor={colors.warmGray}
                    />
                  </View>
                  <View style={styles.inputWrapper}>
                    <Icon name="phone" size={18} color={colors.warmGray} style={styles.inputIcon} />
                    <TextInput
                      style={styles.input}
                      placeholder="Phone Number"
                      value={userPhone}
                      onChangeText={setUserPhone}
                      keyboardType="phone-pad"
                      placeholderTextColor={colors.warmGray}
                    />
                  </View>
                </View>
              </View>
            )}

            {/* Payment Method */}
            <View style={styles.bankCard}>
              <View style={styles.cardHeader}>
                <Icon name="account-balance" size={18} color={colors.beeYellow} />
                <Text style={styles.cardTitle}>Payment Method</Text>
              </View>
              <TouchableOpacity
                style={styles.selector}
                onPress={() => setIsBankDropdownOpen(!isBankDropdownOpen)}
              >
                {selectedBank ? (
                  <View style={styles.selectorContent}>
                    {(() => {
                      const iconKey = getBankIconKey(selectedBank.id) || getBankIconKey(selectedBank.name);
                      return iconKey ? (
                        <Image
                          source={bankIcons[iconKey]}
                          style={styles.bankIcon}
                          resizeMode="contain"
                        />
                      ) : null;
                    })()}
                    <Text style={styles.selectorText}>{selectedBank.name}</Text>
                  </View>
                ) : (
                  <Text style={styles.selectorPlaceholder}>Choose your bank</Text>
                )}
                <Icon 
                  name={isBankDropdownOpen ? "expand-less" : "expand-more"} 
                  size={24} 
                  color={colors.charcoal} 
                />
              </TouchableOpacity>

              {isBankDropdownOpen && (
                <View style={styles.optionsList}>
                  {banks.map((bank, index) => {
                    const isCBEBirr = bank.name.toLowerCase().includes('cbebirr') || bank.name.toLowerCase().includes('cbe birr');
                    const isEthioSwitch = bank.name.toLowerCase().includes('ethioswitch') || bank.name.toLowerCase().includes('ethio switch');
                    const isGatewayBank = isCBEBirr || isEthioSwitch;
                    return (
                      <TouchableOpacity
                        key={bank.id}
                        style={[
                          styles.option,
                          index === banks.length - 1 && { borderBottomWidth: 0 },
                        ]}
                        onPress={() => {
                          setSelectedBank(bank);
                          setIsBankDropdownOpen(false);
                        }}
                      >
                        {(() => {
                          const iconKey = getBankIconKey(bank.id) || getBankIconKey(bank.name);
                          return iconKey ? (
                            <Image
                              source={bankIcons[iconKey]}
                              style={styles.bankIcon}
                              resizeMode="contain"
                            />
                          ) : null;
                        })()}
                        <Text style={styles.optionText}>{bank.name}</Text>
                        {isGatewayBank && (
                          <View style={{ 
                            backgroundColor: '#10B981', 
                            paddingHorizontal: 8, 
                            paddingVertical: 4, 
                            borderRadius: 6,
                            marginLeft: 8
                          }}>
                            <Text style={{ color: '#FFFFFF', fontSize: 11, fontWeight: '700' }}>FASTEST</Text>
                          </View>
                        )}
                        {selectedBank?.id === bank.id && (
                          <Icon name="check-circle" size={20} color={colors.success} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {selectedBank && !(
                selectedBank.name.toLowerCase().includes('cbebirr') || 
                selectedBank.name.toLowerCase().includes('cbe birr') ||
                selectedBank.name.toLowerCase().includes('ethioswitch') ||
                selectedBank.name.toLowerCase().includes('ethio switch') ||
                selectedBank.name.toLowerCase().includes('telebirr')
              ) && (
                <View style={styles.bankDetails}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Account Name</Text>
                    <Text style={styles.detailValue}>{selectedBank.accountHolderName}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Account Number</Text>
                    <Text style={styles.detailValue}>{selectedBank.accountNumber}</Text>
                  </View>
                </View>
              )}

              {/* EthioSwitch Supported Cards Info */}
              {selectedBank && (
                selectedBank.name.toLowerCase().includes('ethioswitch') ||
                selectedBank.name.toLowerCase().includes('ethio switch')
              ) && (
                <View style={styles.supportedCardsContainer}>
                  <View style={styles.supportedCardsHeader}>
                    <Icon name="credit-card" size={16} color={colors.beeYellow} />
                    <Text style={styles.supportedCardsTitle}>Supported Cards</Text>
                  </View>
                  <Text style={styles.supportedCardsText}>
                    Dashen • Amhara • Wegagen • Tsedey • Bunna • ZamZam
                  </Text>
                  <View style={styles.supportedCardsNote}>
                    <Icon name="info-outline" size={14} color={colors.warmGray} />
                    <Text style={styles.supportedCardsNoteText}>
                      You can use any of these bank cards for payment
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Submit Button */}
            <View style={styles.submitSection}>
              <TouchableOpacity
                style={[styles.submitButton, (submitting || !selectedBank || (selectedBank && !(
                  selectedBank.name.toLowerCase().includes('cbebirr') || 
                  selectedBank.name.toLowerCase().includes('cbe birr') ||
                  selectedBank.name.toLowerCase().includes('ethioswitch') ||
                  selectedBank.name.toLowerCase().includes('ethio switch') ||
                  selectedBank.name.toLowerCase().includes('telebirr')
                ) && (!userName || !userPhone))) && styles.submitButtonDisabled]}
                onPress={handleSubmitPayment}
                disabled={submitting || !selectedBank || (selectedBank && !(
                  selectedBank.name.toLowerCase().includes('cbebirr') || 
                  selectedBank.name.toLowerCase().includes('cbe birr') ||
                  selectedBank.name.toLowerCase().includes('ethioswitch') ||
                  selectedBank.name.toLowerCase().includes('ethio switch') ||
                  selectedBank.name.toLowerCase().includes('telebirr')
                ) && (!userName || !userPhone))}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon name="lock" size={18} color={colors.deepNavy} />
                    <Text style={styles.submitButtonText}>Complete Secure Payment</Text>
                    <Icon name="arrow-forward" size={18} color={colors.deepNavy} />
                  </>
                )}
              </TouchableOpacity>

              <View style={styles.footerInfo}>
                <Icon name="verified-user" size={14} color={colors.success} />
                <Text style={styles.footerText}>
                  Secure payment • Activates within 24 hours
                </Text>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Success Modal */}
      <Modal
        visible={showSuccessModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowSuccessModal(false);
          navigation.goBack();
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="check-circle" size={60} color={colors.success} />
              <Text style={styles.modalTitle}>Payment Submitted Successfully!</Text>
            </View>
            
            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                Your payment request is being processed and verified.
              </Text>
              
              <View style={styles.modalDetailBox}>
                <View style={styles.modalDetailRow}>
                  <Text style={styles.modalDetailLabel}>Reference:</Text>
                  <Text style={styles.modalDetailValue}>{successData?.reference}</Text>
                </View>
                <View style={styles.modalDetailRow}>
                  <Text style={styles.modalDetailLabel}>Amount:</Text>
                  <Text style={styles.modalDetailValue}>{successData?.amount} ETB</Text>
                </View>
                <View style={styles.modalDetailRow}>
                  <Text style={styles.modalDetailLabel}>Bank:</Text>
                  <Text style={styles.modalDetailValue}>{successData?.bankName}</Text>
                </View>
              </View>

              <View style={styles.modalInfoBox}>
                <Icon name="email" size={20} color={colors.beeYellow} style={styles.modalIcon} />
                <Text style={styles.modalInfoText}>
                  A confirmation email with payment details has been sent to {successData?.userEmail}
                </Text>
              </View>

              <View style={styles.modalInfoBox}>
                <Icon name="schedule" size={20} color={colors.beeYellow} style={styles.modalIcon} />
                <Text style={styles.modalInfoText}>
                  Your subscription will be activated within 1-3 business days after verification.
                </Text>
              </View>

              <Text style={styles.modalSubtext}>
                You'll receive an email notification once your payment is confirmed.
              </Text>
            </View>

            <TouchableOpacity
              style={styles.modalButton}
              onPress={() => {
                setShowSuccessModal(false);
                navigation.goBack();
              }}
            >
              <Text style={styles.modalButtonText}>Got it!</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>


    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.warmGray,
  },
  jobsToolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    zIndex: 10,
  },
  toolbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  toolbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  toolbarTitle: {
    fontSize: 20,
    fontWeight: '700',
    fontFamily: fonts.bold,
  },
  toolbarIconButton: {
    padding: 8,
    marginRight: 8,
  },
  mobileMenu: {
    position: 'absolute',
    top: 60,
    right: 10,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 1000,
    minWidth: 200,
  },
  mobileMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
  },
  mobileMenuText: {
    fontSize: 15,
    fontWeight: '500',
    fontFamily: fonts.medium,
  },
  content: {
    flex: 1,
    backgroundColor: '#FAFBFC',
  },
  // Reference Card Styles
  referenceCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  referenceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 10,
  },
  referenceLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.charcoal,
    fontFamily: fonts.semiBold,
  },
  section: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.charcoal,
    marginBottom: 12,
  },
  referenceBox: {
    backgroundColor: '#FFF9E6',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.beeYellow,
    borderStyle: 'dashed',
  },
  referenceCode: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.deepNavy,
    letterSpacing: 4,
    fontFamily: fonts.bold,
  },
  referenceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    gap: 6,
  },
  referenceHint: {
    fontSize: 13,
    color: colors.warmGray,
    fontStyle: 'italic',
  },
  // Pricing Card Styles
  pricingCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  pricingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 10,
  },
  pricingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.charcoal,
    fontFamily: fonts.semiBold,
  },
  planDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  planBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cream,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    gap: 8,
  },
  planName: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.deepNavy,
    fontFamily: fonts.bold,
  },
  amountContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
  },
  amountValue: {
    fontSize: 36,
    fontWeight: '800',
    color: colors.success,
    fontFamily: fonts.bold,
  },
  amountCurrency: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.success,
  },
  periodSection: {
    marginTop: 4,
  },
  periodLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.charcoal,
    marginBottom: 12,
  },
  toggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    padding: 4,
    gap: 6,
  },
  toggleButton: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    gap: 6,
  },
  toggleButtonActive: {
    backgroundColor: colors.white,
    shadowColor: colors.beeYellow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 2,
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.warmGray,
  },
  toggleTextActive: {
    color: colors.deepNavy,
    fontWeight: '700',
  },
  savingBadge: {
    backgroundColor: colors.success,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },
  savingText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '700',
  },
  // Info Card Styles
  infoCard: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 10,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.charcoal,
    fontFamily: fonts.semiBold,
  },
  inputGroup: {
    gap: 12,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingHorizontal: 14,
  },
  inputIcon: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 16,
    color: colors.charcoal,
  },
  // Bank Card Styles
  bankCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  // Submit Section Styles
  submitSection: {
    marginTop: 8,
    marginBottom: 20,
  },
  submitButton: {
    backgroundColor: colors.beeYellow,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    shadowColor: colors.beeYellow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  submitButtonDisabled: {
    backgroundColor: colors.warmGray,
    shadowOpacity: 0.1,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.deepNavy,
    fontFamily: fonts.bold,
  },
  footerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    gap: 8,
  },
  footerText: {
    fontSize: 13,
    color: colors.warmGray,
    textAlign: 'center',
  },
  hint: {
    fontSize: 13,
    color: colors.warmGray,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  value: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.charcoal,
    marginTop: 4,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  selectorText: {
    fontSize: 16,
    color: colors.charcoal,
    fontWeight: '500',
  },
  selectorPlaceholder: {
    fontSize: 16,
    color: colors.warmGray,
  },
  bankIcon: {
    width: 28,
    height: 28,
    marginRight: 12,
    borderRadius: 14,
  },
  optionsList: {
    marginTop: 8,
    backgroundColor: colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  optionText: {
    fontSize: 16,
    color: colors.charcoal,
    flex: 1,
  },
  bankDetails: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: colors.warmGray,
  },
  detailValue: {
    fontSize: 14,
    color: colors.charcoal,
    fontWeight: '600',
  },
  // Supported Cards Styles (EthioSwitch)
  supportedCardsContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    backgroundColor: '#F0F9FF',
    padding: 14,
    borderRadius: 8,
  },
  supportedCardsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  supportedCardsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.charcoal,
    fontFamily: fonts.semiBold,
  },
  supportedCardsText: {
    fontSize: 15,
    color: colors.deepNavy,
    fontWeight: '500',
    marginBottom: 10,
    lineHeight: 22,
  },
  supportedCardsNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 4,
  },
  supportedCardsNoteText: {
    flex: 1,
    fontSize: 12,
    color: colors.warmGray,
    fontStyle: 'italic',
  },
  // Old styles kept for compatibility
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.charcoal,
    marginTop: 12,
    textAlign: 'center',
  },
  modalBody: {
    marginBottom: 20,
  },
  modalText: {
    fontSize: 16,
    color: colors.warmGray,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalDetailBox: {
    backgroundColor: colors.lightGray,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  modalDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalDetailLabel: {
    fontSize: 14,
    color: colors.warmGray,
    fontWeight: '600',
  },
  modalDetailValue: {
    fontSize: 14,
    color: colors.charcoal,
    fontWeight: '700',
  },
  modalInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.cream,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  modalIcon: {
    marginRight: 10,
    marginTop: 2,
  },
  modalInfoText: {
    flex: 1,
    fontSize: 14,
    color: colors.charcoal,
    lineHeight: 20,
  },
  modalSubtext: {
    fontSize: 13,
    color: colors.warmGray,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  modalButton: {
    backgroundColor: colors.beeYellow,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    shadowColor: colors.beeYellow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
});

export default PaymentScreen;
